/**
 * Creates a new error with the specified name and message.
 *
 * @param {string} name - The name of the error.
 * @param {string} message - The message of the error.
 * @returns {Error} The newly created error object.
 */
export const createError = (name: string, message: string) => {
  const error = new Error(message);
  error.name = name;
  return error;
};

/**
 * Creates a FetchError with a specified message and status code.
 *
 * @param {string} message - The error message.
 * @param {number} statusCode - The HTTP status code associated with the error.
 * @returns {Error} The created FetchError with the specified message and status code.
 */
export const createFetchError = (
  message: string,
  statusCode: string | number,
) => {
  const error = createError("FetchError", message);
  (error as any).statusCode = statusCode;
  return error;
};

/**
 * Creates a fetch error with a 404 Not Found status code.
 *
 * @param {string} message - The error message to be included in the fetch error.
 * @returns {Error} The fetch error with a 404 status code.
 */
export const createFetchNotFoundError = (message: string) => {
  return createFetchError(message, 404);
};

/**
 * Creates an authentication error with the specified message.
 *
 * @param {string} message - The error message to be associated with the authentication error.
 * @returns {Error} The created authentication error.
 */
export const createAuthError = (message: string) => {
  return createError("AuthError", message);
};

/**
 * Creates an AffiliateError with the given message.
 *
 * @param {string} message - The error message to be associated with the AffiliateError.
 * @returns {Error} The created AffiliateError.
 */
export const createAffiliateError = (message: string) => {
  return createError("AffiliateError", message);
};

/**
 * Creates a new FormatError with the specified message.
 *
 * @param {string} message - The error message to be associated with the FormatError.
 * @returns {Error} The newly created FormatError.
 */
export const createFormatError = (message: string) => {
  return createError("FormatError", message);
};

const errorUtils = {
  createFetchError,
  createFetchNotFoundError,
  createAuthError,
  createAffiliateError,
  createFormatError,
};

export default errorUtils;
